import type { BillingInterval, PlanId } from '@tandemone/types'
import { PLAN_CATALOG } from '@tandemone/types'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'

import { intervalLabel } from './plan-price'

export type PlanChosenMode = 'free' | 'saved' | 'invoice'

/* a toast fired and the page navigated in the same tick, so the one screen that
 * explains an invoice-billed country was gone in about a second. this has to be
 * dismissed, and the dismissal is what advances. */
export function PlanChosenDialog({
  mode,
  planId,
  interval,
  onDismiss
}: {
  mode: PlanChosenMode | null
  planId: PlanId | null
  interval: BillingInterval
  onDismiss: () => void
}) {
  if (!mode || !planId) return null
  const label = PLAN_CATALOG[planId].label
  const cadence = intervalLabel(interval).toLowerCase()

  return (
    <Dialog open onOpenChange={open => !open && onDismiss()}>
      <DialogContent className='sm:max-w-[460px]'>
        <DialogHeader>
          <DialogTitle>
            {mode === 'free' ? `You’re on ${label}` : `${label} saved`}
          </DialogTitle>
          <DialogDescription asChild>
            <div className='flex flex-col gap-3 text-[14px]'>
              {mode === 'free' && (
                <p>
                  Your profile stays listed and you keep receiving RFIs. There
                  is nothing to pay.
                </p>
              )}

              {mode === 'saved' && (
                <>
                  <p>
                    You picked {label}, billed {cadence}.
                  </p>
                  <p>
                    Nothing is charged yet. We charge the card you saved once
                    staff approve your profile.
                  </p>
                </>
              )}

              {/* leads with how billing works rather than with what we will
                  not do, and never frames it as something about their
                  country. */}
              {mode === 'invoice' && (
                <>
                  <p>
                    You picked {label}, billed {cadence}.
                  </p>
                  <p>
                    This plan is billed by invoice. You will receive one every{' '}
                    {interval}, and we will send a reminder before it is due.
                  </p>
                  <p>
                    Once your profile is approved, we will email your first
                    invoice. Your plan starts as soon as it is paid.
                  </p>
                </>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={onDismiss}>Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
